// Stock Transfers - Step 3: Review & Complete
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  CheckCircle2,
  ArrowRightLeft,
  ArrowRight,
  Info,
  Package,
  MapPin,
  Calendar,
  FileText,
  User,
  Hash,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { TransferConfigData } from "./stock-transfers-step2";
import { format } from "date-fns";
import { saveTransfer, updateTransfer } from "@/lib/transfer-storage";

interface StockTransfersStep3PageProps {
  pageParams?: {
    configData?: TransferConfigData;
    isEditing?: boolean;
    transferId?: string;
  };
}

export function StockTransfersStep3Page({ pageParams }: StockTransfersStep3PageProps) {
  const { navigateTo } = useNavigation();
  const configData = pageParams?.configData;
  const isEditing = pageParams?.isEditing || false;
  const transferId = pageParams?.transferId;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if no config data
  useEffect(() => {
    if (!configData) {
      toast.error("Please complete previous steps first");
      navigateTo("stock-transfers/create/step1");
    }
  }, [configData, navigateTo]);

  if (!configData) {
    return null;
  }

  const handleBack = () => {
    navigateTo("stock-transfers/create/step2", { 
      setupData: configData,
      configData,
      isEditing,
      transferId,
    });
  };

  const handleSaveAsDraft = async () => {
    setIsSubmitting(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));
      
      // Save or update transfer as draft
      let savedTransfer;
      if (isEditing && transferId) {
        savedTransfer = updateTransfer(transferId, configData, "draft");
        if (!savedTransfer) {
          throw new Error("Failed to update transfer");
        }
      } else {
        savedTransfer = saveTransfer(configData, "draft");
      }
      
      toast.success(
        <div>
          <div className="font-semibold">
            {isEditing ? "Transfer updated!" : "Transfer saved as draft!"}
          </div>
          <div className="text-sm mt-1">
            Reference: {savedTransfer.reference}
          </div>
        </div>
      );
      navigateTo("stock-transfers");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDispatchTransfer = async () => {
    setIsSubmitting(true);
    try {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Calculate expected arrival date (3 days from now as default)
      const expectedArrival = new Date();
      expectedArrival.setDate(expectedArrival.getDate() + 3);
      const expectedArrivalDate = expectedArrival.toISOString().split('T')[0];
      
      // Save or update transfer as in-transit
      let savedTransfer;
      if (isEditing && transferId) {
        savedTransfer = updateTransfer(transferId, configData, "in-transit", expectedArrivalDate);
        if (!savedTransfer) {
          throw new Error("Failed to update transfer");
        }
      } else {
        savedTransfer = saveTransfer(configData, "in-transit", expectedArrivalDate);
      }
      
      toast.success(
        <div>
          <div className="font-semibold">Transfer dispatched successfully!</div>
          <div className="text-sm mt-1">
            {savedTransfer.itemCount} items dispatched ({savedTransfer.totalQuantity} units)
          </div>
          <div className="text-sm">Reference: {savedTransfer.reference}</div>
          <div className="text-sm text-muted-foreground mt-1">
            Expected arrival: {format(expectedArrival, "MMM dd, yyyy")}
          </div>
        </div>
      );
      navigateTo("stock-transfers");
    } catch (error) {
      console.error("Error dispatching transfer:", error);
      toast.error("Failed to dispatch transfer. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate totals
  const totalQuantity = configData.transfers.reduce((sum, t) => sum + t.quantity, 0);
  const uniqueLocations = new Set([
    ...configData.transfers.map((t) => t.sourceLocation),
    ...configData.transfers.map((t) => t.destinationLocation),
  ]).size;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 flex items-center justify-between pb-4 border-b bg-background">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Review and complete your transfer
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto py-6">
        <div className="max-w-6xl mx-auto w-full space-y-6">
          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
                  ✓
                </div>
                <span className="text-sm text-muted-foreground">Select Items</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
                  ✓
                </div>
                <span className="text-sm text-muted-foreground">Configure Transfers</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
                  3
                </div>
                <span className="text-sm font-medium">Review & Complete</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Review your transfer summary before dispatching
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                <span>Total Items</span>
              </div>
              <div className="text-2xl font-bold">{configData.transfers.length}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Hash className="h-4 w-4" />
                <span>Total Units</span>
              </div>
              <div className="text-2xl font-bold">{totalQuantity}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>Unique Locations</span>
              </div>
              <div className="text-2xl font-bold">{uniqueLocations}</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            💡 Tip: Review all transfer details carefully before dispatching
          </p>
        </CardContent>
          </Card>

          {/* Transfer Details */}
          <Card className="mb-6">
        <CardHeader>
          <CardTitle>Transfer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>Reference</span>
              </div>
              <div className="font-medium">{configData.reference}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Transfer Date</span>
              </div>
              <div className="font-medium">
                {format(new Date(configData.date), "dd MMM yyyy")}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                <span>Total Items</span>
              </div>
              <div className="font-medium">{configData.transfers.length}</div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Hash className="h-4 w-4" />
                <span>Total Units</span>
              </div>
              <div className="font-medium">{totalQuantity}</div>
            </div>

            {configData.notes && (
              <div className="space-y-1 md:col-span-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>Notes</span>
                </div>
                <div className="text-sm">{configData.notes}</div>
              </div>
            )}
          </div>
        </CardContent>
          </Card>

          {/* Transfer Items */}
          <Card className="flex-1">
        <CardHeader>
          <CardTitle>Transfer Items</CardTitle>
          <CardDescription>
            Review all {configData.transfers.length} transfers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(() => {
              // Group transfers by item code to show split transfers together
              const groupedTransfers: { [key: string]: typeof configData.transfers } = {};
              configData.transfers.forEach((transfer) => {
                const key = transfer.originalItemCode || transfer.itemCode;
                if (!groupedTransfers[key]) {
                  groupedTransfers[key] = [];
                }
                groupedTransfers[key].push(transfer);
              });

              return Object.entries(groupedTransfers).map(([itemCode, transfers]) => {
                const isMultiple = transfers.length > 1;
                const totalQuantity = transfers.reduce((sum, t) => sum + t.quantity, 0);
                
                // Determine if it's a split (multiple sources) or distribution (multiple destinations)
                const uniqueSources = new Set(transfers.map(t => t.sourceLocation));
                const uniqueDestinations = new Set(transfers.map(t => t.destinationLocation));
                const isSplit = uniqueSources.size > 1 && uniqueDestinations.size === 1;
                const isDistribution = uniqueSources.size === 1 && uniqueDestinations.size > 1;
                const isMixed = uniqueSources.size > 1 && uniqueDestinations.size > 1;

                // Get the color from the first transfer (all transfers of same item should have same color)
                const itemColor = transfers[0].color || { 
                  icon: "text-muted-foreground", 
                  badgeBg: "bg-muted", 
                  badgeText: "text-muted-foreground" 
                };

                return (
                  <Card key={itemCode} className="border-2">
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {/* Item Header */}
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${itemColor.badgeBg}`}>
                              <Package className={`h-5 w-5 ${itemColor.icon}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="font-semibold text-lg">{transfers[0].itemCode}</div>
                                {isMultiple && (
                                  <Badge variant="secondary" className="text-xs">
                                    {transfers.length} Transfers
                                  </Badge>
                                )}
                                {isSplit && (
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                    Consolidation
                                  </Badge>
                                )}
                                {isDistribution && (
                                  <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                                    Distribution
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {transfers[0].itemName}
                              </div>
                            </div>
                          </div>
                          <Badge variant="secondary" className="text-lg px-3 py-1">
                            {totalQuantity} units total
                          </Badge>
                        </div>

                        <Separator />

                        {/* Transfer Routes */}
                        <div className="space-y-3">
                          {transfers.map((transfer, idx) => (
                            <div key={transfer.itemId}>
                              {idx > 0 && <Separator className="my-3" />}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <MapPin className="h-4 w-4" />
                                    <span>From {isMultiple && `(${idx + 1})`}</span>
                                  </div>
                                  <div className="font-medium text-sm">
                                    {transfer.sourceLocation.split("-").map((part) => part.trim()).join(" - ")}
                                  </div>
                                </div>

                                <div className="flex items-center justify-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <ArrowRight className="h-5 w-5 text-muted-foreground" />
                                    <Badge variant="outline" className="text-xs">
                                      {transfer.quantity} units
                                    </Badge>
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <MapPin className="h-4 w-4" />
                                    <span>To {isMultiple && `(${idx + 1})`}</span>
                                  </div>
                                  <div className="font-medium text-sm">
                                    {transfer.destinationLocation.split("-").map((part) => part.trim()).join(" - ")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Summary for split/distribution transfers */}
                        {isMultiple && (
                          <>
                            <Separator />
                            <div className="bg-muted/50 rounded-lg p-3">
                              <div className="text-sm font-medium text-muted-foreground">
                                {isSplit && `📦 Summary: Consolidating ${totalQuantity} units from ${transfers.length} different locations`}
                                {isDistribution && `📤 Summary: Distributing ${totalQuantity} units to ${transfers.length} different locations`}
                                {isMixed && `🔄 Summary: Transferring ${totalQuantity} units across ${transfers.length} different routes`}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              });
            })()}
          </div>
        </CardContent>
          </Card>
        </div>
      </div>

      {/* Sticky Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSaveAsDraft}
              disabled={isSubmitting}
            >
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
            <Button
              onClick={handleDispatchTransfer}
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Dispatch Transfer
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
